import React, { useState, useEffect } from "react";
import { Trash2, X, Plus } from "lucide-react";
import FieldConfigurationAPI from "../../../../lib/api/FieldConfiguration";
import SearchableCodeSelect from "../../../common/SearchableCodeSelect";

// Vendors have code/name, not the picker's default code/description.
const VENDOR_OPTION = {
  formatOptionLabel: (o) => (o.name ? `${o.code} - ${o.name}` : o.code),
  renderOption: (o) => (
    <>
      <div className="searchable-code-select__option-code">{o.code}</div>
      <div className="searchable-code-select__option-name">{o.name}</div>
    </>
  ),
};

// NOTE: shape is inferred from CE's CAPIFilterSettings_ICItems
// (apifilters.h) — 3 MAPRANGEs (items/classes/vendors) + status flag + 7
// value/billing class checkboxes. Not yet verified against a live
// GET /group/{uuid}/filters payload.
function blankRule() {
  return {
    filter_type: "include",
    inv: {
      from: "",
      to: "",
      from_class: "",
      to_class: "",
      from_vendor: "",
      to_vendor: "",
      status: "active",
      value_class_gl: true,
      value_class_non_gl: true,
      value_class_intangible: true,
      billing_class_labor: true,
      billing_class_materials: true,
      billing_class_services: true,
      billing_class_other: true,
    },
  };
}

export default function InventoryItemsFilterModal({
  open,
  groupUuid,
  companyUuid,
  filter,
  onSave,
  onClose,
  readOnly = false,
}) {
  const [rules, setRules] = useState([blankRule()]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    const existing = filter?.rules;
    setRules(
      existing && existing.length > 0
        ? existing.map((r) => ({
            filter_type: r.filter_type || "include",
            inv: {
              from: r.inv?.from ?? "",
              to: r.inv?.to ?? "",
              from_class: r.inv?.from_class ?? "",
              to_class: r.inv?.to_class ?? "",
              from_vendor: r.inv?.from_vendor ?? "",
              to_vendor: r.inv?.to_vendor ?? "",
              status: r.inv?.status ?? "active",
              value_class_gl: r.inv?.value_class_gl ?? true,
              value_class_non_gl: r.inv?.value_class_non_gl ?? true,
              value_class_intangible: r.inv?.value_class_intangible ?? true,
              billing_class_labor: r.inv?.billing_class_labor ?? true,
              billing_class_materials: r.inv?.billing_class_materials ?? true,
              billing_class_services: r.inv?.billing_class_services ?? true,
              billing_class_other: r.inv?.billing_class_other ?? true,
            },
          }))
        : [blankRule()]
    );
    setError(null);
  }, [open, filter]);

  if (!open) return null;

  function updateRule(index, field, value) {
    setRules((prev) =>
      prev.map((r, i) =>
        i === index ? { ...r, inv: { ...r.inv, [field]: value } } : r
      )
    );
  }

  function updateFilterType(index, value) {
    setRules((prev) =>
      prev.map((r, i) => (i === index ? { ...r, filter_type: value } : r))
    );
  }

  function removeRule(index) {
    setRules((prev) =>
      prev.length === 1 ? [blankRule()] : prev.filter((_, i) => i !== index)
    );
  }

  function handleSave() {
    const cleanedRules = rules.map((r) => ({
      filter_type: r.filter_type,
      inv: {
        from: r.inv.from || null,
        to: r.inv.to || null,
        from_class: r.inv.from_class || null,
        to_class: r.inv.to_class || null,
        from_vendor: r.inv.from_vendor || null,
        to_vendor: r.inv.to_vendor || null,
        status: r.inv.status || null,
        value_class_gl: r.inv.value_class_gl,
        value_class_non_gl: r.inv.value_class_non_gl,
        value_class_intangible: r.inv.value_class_intangible,
        billing_class_labor: r.inv.billing_class_labor,
        billing_class_materials: r.inv.billing_class_materials,
        billing_class_services: r.inv.billing_class_services,
        billing_class_other: r.inv.billing_class_other,
      },
    }));

    onSave({ resource: "inv", mode: "filtered", rules: cleanedRules });
  }

  return (
    <div className="jfm-overlay" onClick={onClose}>
      <div className="jfm-container" onClick={(e) => e.stopPropagation()}>
        <div className="jfm-header">
          <span className="jfm-title">Inventory Items Filters</span>
          <button className="jfm-close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="jfm-body">
          <fieldset className="jfm-fieldset" disabled={readOnly}>
            {rules.map((rule, idx) => (
              <div key={idx} className="eqfm-row">
                <select
                  className="jfm-type-select"
                  value={rule.filter_type}
                  onChange={(e) => updateFilterType(idx, e.target.value)}
                >
                  <option value="include">Include</option>
                  <option value="exclude">Exclude</option>
                </select>

                <div className="eqfm-fields">
                  <div className="eqfm-inputs-row">
                    <div className="efm-field">
                      <label>From Item</label>
                      <SearchableCodeSelect
                        value={rule.inv.from}
                        companyUuid={companyUuid}
                        placeholder="(first)"
                        fetchOptions={FieldConfigurationAPI.getInventoryItems}
                        emptyMessage="No inventory items found"
                        disabled={readOnly}
                        onChange={(code) => updateRule(idx, "from", code)}
                      />
                    </div>
                    <div className="efm-field">
                      <label>To Item</label>
                      <SearchableCodeSelect
                        value={rule.inv.to}
                        companyUuid={companyUuid}
                        placeholder="(last)"
                        fetchOptions={FieldConfigurationAPI.getInventoryItems}
                        emptyMessage="No inventory items found"
                        disabled={readOnly}
                        onChange={(code) => updateRule(idx, "to", code)}
                      />
                    </div>
                    <div className="efm-field efm-field--status">
                      <label>Status</label>
                      <select
                        value={rule.inv.status || "active"}
                        onChange={(e) =>
                          updateRule(idx, "status", e.target.value)
                        }
                      >
                        <option value="all">All</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                  </div>

                  <div className="eqfm-inputs-row">
                    <div className="efm-field">
                      <label>From Class</label>
                      <input
                        value={rule.inv.from_class}
                        placeholder="(first)"
                        onChange={(e) =>
                          updateRule(idx, "from_class", e.target.value)
                        }
                      />
                    </div>
                    <div className="efm-field">
                      <label>To Class</label>
                      <input
                        value={rule.inv.to_class}
                        placeholder="(last)"
                        onChange={(e) =>
                          updateRule(idx, "to_class", e.target.value)
                        }
                      />
                    </div>
                    <div className="efm-field">
                      <label>From Vendor</label>
                      <SearchableCodeSelect
                        value={rule.inv.from_vendor}
                        companyUuid={companyUuid}
                        placeholder="(first)"
                        fetchOptions={FieldConfigurationAPI.getVendors}
                        {...VENDOR_OPTION}
                        emptyMessage="No vendors found"
                        disabled={readOnly}
                        onChange={(code) =>
                          updateRule(idx, "from_vendor", code)
                        }
                      />
                    </div>
                    <div className="efm-field">
                      <label>To Vendor</label>
                      <SearchableCodeSelect
                        value={rule.inv.to_vendor}
                        companyUuid={companyUuid}
                        placeholder="(last)"
                        fetchOptions={FieldConfigurationAPI.getVendors}
                        {...VENDOR_OPTION}
                        emptyMessage="No vendors found"
                        disabled={readOnly}
                        onChange={(code) => updateRule(idx, "to_vendor", code)}
                      />
                    </div>
                  </div>

                  <div
                    className="eqfm-checkboxes-row"
                    style={{ flexWrap: "wrap", rowGap: "8px" }}
                  >
                    <label className="eqfm-checkbox-label">
                      <input
                        type="checkbox"
                        checked={rule.inv.value_class_gl}
                        onChange={(e) =>
                          updateRule(idx, "value_class_gl", e.target.checked)
                        }
                      />
                      GL Value Class
                    </label>
                    <label className="eqfm-checkbox-label">
                      <input
                        type="checkbox"
                        checked={rule.inv.value_class_non_gl}
                        onChange={(e) =>
                          updateRule(
                            idx,
                            "value_class_non_gl",
                            e.target.checked
                          )
                        }
                      />
                      Non-GL Value Class
                    </label>
                    <label className="eqfm-checkbox-label">
                      <input
                        type="checkbox"
                        checked={rule.inv.value_class_intangible}
                        onChange={(e) =>
                          updateRule(
                            idx,
                            "value_class_intangible",
                            e.target.checked
                          )
                        }
                      />
                      Intangible Value Class
                    </label>
                    <label className="eqfm-checkbox-label">
                      <input
                        type="checkbox"
                        checked={rule.inv.billing_class_labor}
                        onChange={(e) =>
                          updateRule(
                            idx,
                            "billing_class_labor",
                            e.target.checked
                          )
                        }
                      />
                      Labor Billing Class
                    </label>
                    <label className="eqfm-checkbox-label">
                      <input
                        type="checkbox"
                        checked={rule.inv.billing_class_materials}
                        onChange={(e) =>
                          updateRule(
                            idx,
                            "billing_class_materials",
                            e.target.checked
                          )
                        }
                      />
                      Materials Billing Class
                    </label>
                    <label className="eqfm-checkbox-label">
                      <input
                        type="checkbox"
                        checked={rule.inv.billing_class_services}
                        onChange={(e) =>
                          updateRule(
                            idx,
                            "billing_class_services",
                            e.target.checked
                          )
                        }
                      />
                      Services Billing Class
                    </label>
                    <label className="eqfm-checkbox-label">
                      <input
                        type="checkbox"
                        checked={rule.inv.billing_class_other}
                        onChange={(e) =>
                          updateRule(
                            idx,
                            "billing_class_other",
                            e.target.checked
                          )
                        }
                      />
                      Other Billing Class
                    </label>
                  </div>
                </div>

                {!readOnly && (
                  <button
                    className="jfm-delete"
                    onClick={() => removeRule(idx)}
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            ))}
          </fieldset>
        </div>

        {error && <div className="jfm-error">{error}</div>}

        <div className="jfm-footer">
          {readOnly ? (
            <div />
          ) : (
            <button
              className="jfm-add-row"
              onClick={() => setRules((prev) => [...prev, blankRule()])}
            >
              <Plus size={14} /> Add Row
            </button>
          )}
          <div className="jfm-footer-actions">
            {readOnly ? (
              <button className="jfm-cancel" onClick={onClose}>
                Close
              </button>
            ) : (
              <>
                <button
                  className="jfm-cancel"
                  onClick={onClose}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  className="jfm-save"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
